import React from 'react'
import {
  Box,
  Snackbar,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  styled,
  TablePagination,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { DatePicker } from '@material-ui/pickers'
import { Edit as EditIcon } from '@mui/icons-material'
import MuiAlert from '@mui/material/Alert'
import { Batch, IBatch, IPremix } from 'interfaces/IPremix'
import API from '@aws-amplify/api'
import { useSelector } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'
import ConfirmDialog from 'components/confirmDialog'
import { getFriendlyErrorMessage } from 'utils/errorHandling'
import {
  STOCK_CARD_TYPE_INGREDIENTS,
  STOCK_CARD_TYPE_PREMIX,
} from 'enums/stockCard'
import { IStockCard } from 'interfaces/IStockCard'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'
import { isUserSuperuser } from 'shared/auth'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import { formatDecimalAuto } from 'shared/numberFormatter'


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#f4f2f7',
  },
  paddingTop: '20px',
}))

export default function ManageBatchMixing(props: {
  isIncompleteOnly?: boolean
  isCompleteOnly?: boolean
  premixId?: string
  isMixing?: boolean
  isReadOnly?: boolean
  onlyProduction?: boolean
  department?: string
  excludeVirtual?: boolean
}) {
  const {
    isMixing = false,
    isIncompleteOnly = false,
    isCompleteOnly = false,
    premixId = '',
    isReadOnly = false,
    onlyProduction = false,
    department,
    excludeVirtual = false,
  } = props
  const [batchData, setBatchData] = React.useState<IBatch>(new Batch({}))
  const [batches, setBatches] = React.useState<IBatch[]>([])
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [premixDepartment, setPremixDepartment] = React.useState<string>(
    'kosmetik'
  )
  const [isBatchSelected, setIsBatchSelected] = React.useState(false)
  const [searchedPremix, setSearchedPremix] = React.useState(premixId ?? '')
  const [searchedPremixDisplay, setSearchedPremixDisplay] = React.useState(
    premixId ?? ''
  )
  const [isShowingCreatedBatch, setIsShowingCreatedBatch] = React.useState(
    isCompleteOnly ? true : isIncompleteOnly ? false : !isMixing
  )
  const [isShowingEmptyBatch, setIsShowingEmptyBatch] = React.useState(
    isIncompleteOnly ? true : isCompleteOnly ? false : !isMixing
  )
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false)
  const [isConfirmCancelMixingOpen, setIsConfirmCancelMixingOpen] =
    React.useState(false)
  const [batchToCancel, setBatchToCancel] = React.useState<IBatch | null>(null)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [totalCount, setTotalCount] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(20)

  React.useEffect(() => {
    if (isCompleteOnly) {
      setIsShowingCreatedBatch(true)
      setIsShowingEmptyBatch(false)
    } else if (isIncompleteOnly) {
      setIsShowingCreatedBatch(false)
      setIsShowingEmptyBatch(true)
    }
  }, [isCompleteOnly, isIncompleteOnly])

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }
  const userGroups = useSelector((state: IReduxState) => state.userGroups || [])
  const isSuperuser = isUserSuperuser(userGroups)
  const [isEditBatchDialogOpen, setIsEditBatchDialogOpen] = React.useState(false)
  const [editingBatchNumber, setEditingBatchNumber] = React.useState('')
  const [newBatchNumber, setNewBatchNumber] = React.useState('')

  const handleDialogClose = () => {
    setIsBatchSelected(false)
    setBatchData(new Batch({}))
  }

  const handleBatchFieldChange = (value: any, key: string): void => {
    setBatchData({ ...batchData, [key]: value })
  }

  const updateBatch = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    setIsLoading(true)
    const batchNumberToComplete = batchData.batchNumber

    API.post('products', `/premix/completeBatch/${batchNumberToComplete}`, {
      body: {
        ...batchData,
      },
    })
      .then((response) => {
        // Remove the completed batch from the local list immediately so the
        // row disappears without a full page refresh. Also refresh from the
        // server to keep state fully synchronized.
        setBatches((prev) =>
          prev.filter((b) => b.batchNumber !== batchNumberToComplete)
        )
        getBatches(
          isShowingEmptyBatch,
          isShowingCreatedBatch,
          searchedPremix,
          currentPage,
          rowsPerPage
        )
        setAlertMessage(
          `Berhasil Tandai Mixing Selesai untuk ${batchData.productionDisplayId}`
        )
        setIsSuccessSnackbarOpen(true)
        setBatchData(new Batch({}))
        setIsBatchSelected(false)
      })
      .catch((error) => {
        setAlertMessage(getFriendlyErrorMessage(error, 'Gagal menambah info bets.'))
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  const getBatches = React.useCallback(
    (
      isShowingEmptyBatch: boolean,
      isShowingCreatedBatch: boolean,
      premixId: string,
      page: number = 1,
      pageSize: number = 20
    ) => {
      if (!isShowingCreatedBatch && !isShowingEmptyBatch) {
        setBatches([])
        setTotalCount(0)
        return
      }
      const queryStringParameters: any = {}
      if (premixId) {
        queryStringParameters.premixId = premixId
      }
      queryStringParameters.isShowingEmptyBatch = isShowingEmptyBatch
      queryStringParameters.isShowingCreatedBatch = isShowingCreatedBatch
      queryStringParameters.pageSize = pageSize
      queryStringParameters.page = page
      queryStringParameters.includeStockCards = true
      if (onlyProduction) {
        queryStringParameters.onlyProduction = true
      }
      if (department) {
        queryStringParameters.department = department
      }
      if (excludeVirtual) {
        queryStringParameters.excludeVirtual = true
      }
      setIsLoading(true)
      API.get('products', '/premix/getBatches', {
        queryStringParameters,
      })
        .then((response: { batches: IBatch[]; totalCount: number }) => {
          setBatches(response.batches)
          setTotalCount(response.totalCount)
        })
        .catch((error) => {
          setIsFailureSnackbarOpen(true)
          setAlertMessage('Gagal memuat no. bets.')
          console.error(error.response)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [onlyProduction, department, excludeVirtual]
  )

  const handleDeleteBatch = () => {
    const batchNumberToDelete = batchData.batchNumber

    setIsLoading(true)
    API.del('products', `/premix/deleteBatch/${batchNumberToDelete}`, {})
      .then((response) => {
        setAlertMessage('Berhasil menghapus no. bets.')
        setIsSuccessSnackbarOpen(true)
        // getBatches()
      })
      .catch((error) => {
        setAlertMessage('Gagal menghapus no. bets.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleEditBatchNumber = (batchNumber: string) => {
    setEditingBatchNumber(batchNumber)
    setNewBatchNumber(batchNumber)
    setIsEditBatchDialogOpen(true)
  }

  const handleUpdateBatchNumber = () => {
    if (!newBatchNumber || newBatchNumber === editingBatchNumber) {
      setAlertMessage('No. bets baru harus berbeda dari yang lama.')
      setIsFailureSnackbarOpen(true)
      return
    }

    setIsLoading(true)
    API.post('products', '/premix/updateBatchNumber', {
      body: {
        oldBatchNumber: editingBatchNumber,
        newBatchNumber: newBatchNumber,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil mengubah no. bets.')
        setIsSuccessSnackbarOpen(true)
        setIsEditBatchDialogOpen(false)
        getBatches(
          isShowingEmptyBatch,
          isShowingCreatedBatch,
          searchedPremix,
          currentPage,
          rowsPerPage
        )
      })
      .catch((error) => {
        setAlertMessage('Gagal mengubah no. bets.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCancelMixingComplete = async () => {
    if (!batchToCancel) return

    setIsLoading(true)
    try {
      await API.post('products', `/premix/cancelMixingComplete`, {
        body: { batchNumber: batchToCancel.batchNumber },
      })
      setAlertMessage('Aksi mixing berhasil dibatalkan')
      setIsSuccessSnackbarOpen(true)
      getBatches(
        isShowingEmptyBatch,
        isShowingCreatedBatch,
        searchedPremix,
        currentPage,
        rowsPerPage
      )
    } catch (error) {
      console.error(error)
      setAlertMessage('Gagal membatalkan aksi mixing')
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsLoading(false)
      setIsConfirmCancelMixingOpen(false)
      setBatchToCancel(null)
    }
  }

  const handleOpenCancelMixingDialog = (batch: IBatch) => {
    setBatchToCancel(batch)
    setIsConfirmCancelMixingOpen(true)
  }

  const handleSelectBatch = (batch: IBatch) => {
    setIsBatchSelected(true)
    const productionDate = batch.productionDate
      ? new Date(batch.productionDate)
      : new Date()

    const expiryDate = batch.expiryDate ? new Date(batch.expiryDate) : new Date()
    if (!batch.expiryDate) {
      expiryDate.setFullYear(expiryDate.getFullYear() + 2)
    }

    setBatchData({
      ...batch,
      productionDate,
      expiryDate,
    })

    API.get('products', `/premix/getPremix/${batch.premixId}`, {
      queryStringParameters: {
        showInactive: true,
      },
    })
      .then((response: IPremix[]) => {
        if (response && response.length > 0) {
          setPremixDepartment(response[0].department || 'kosmetik')
        }
      })
      .catch((err: any) => console.error(err))
  }

  const isProcessBatchEnabled = (batch: IBatch) => {
    const relevantStockCards = (batch.stockCards ?? []).filter(
      (stockCard) =>
        (stockCard.type === STOCK_CARD_TYPE_INGREDIENTS ||
          stockCard.type === STOCK_CARD_TYPE_PREMIX) &&
        stockCard.inOutDirection === 'OUT'
    )

    // Must have at least one OUT stock card, and ALL must be confirmed
    return (
      relevantStockCards.length > 0 &&
      relevantStockCards.every((stockCard) => stockCard.isConfirmed)
    )
  }

  /**
   * Returns all OUT stock cards (ingredients + premix) that are not yet confirmed.
   * These are the "Dokumen Terkait" blocking the mixing process.
   */
  const getUnconfirmedOutStockCards = (batch: IBatch): IStockCard[] => {
    return (batch.stockCards ?? []).filter(
      (sc) =>
        (sc.type === STOCK_CARD_TYPE_INGREDIENTS ||
          sc.type === STOCK_CARD_TYPE_PREMIX) &&
        sc.inOutDirection === 'OUT' &&
        !sc.isConfirmed
    )
  }
  const isBatchCompleted = (batch: IBatch) => {
    const hasIncomingPremixStockCard = batch.stockCards?.some(
      (sc) => sc.type === STOCK_CARD_TYPE_PREMIX && sc.inOutDirection === 'IN'
    )

    // A batch should ONLY be considered fully completed (hiding the "Proses Bets" button)
    // if it has BOTH a production date and produced quantity, OR if a completion
    // stock card (IN) has already been created for it.
    return Boolean(
      (batch.producedQuantity && batch.productionDate) ||
        hasIncomingPremixStockCard
    )
  }

  React.useEffect(() => {
    getBatches(
      isShowingEmptyBatch,
      isShowingCreatedBatch,
      searchedPremix,
      currentPage,
      rowsPerPage
    )
  }, [
    isShowingEmptyBatch,
    isShowingCreatedBatch,
    searchedPremix,
    currentPage,
    rowsPerPage,
    getBatches,
  ])


  return (
    <Box>
      <Paper>
        {!isIncompleteOnly && !isCompleteOnly && !excludeVirtual && (
          <Box p={2} pb={0}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isShowingEmptyBatch}
                  color="primary"
                  onChange={(e) => {
                    setIsShowingEmptyBatch(e.target.checked)
                  }}
                />
              }
              label={
                <Typography variant="h6">Bets Yang Belum Dibuat</Typography>
              }
              labelPlacement="end"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isShowingCreatedBatch}
                  color="primary"
                  onChange={(e) => {
                    setIsShowingCreatedBatch(e.target.checked)
                  }}
                />
              }
              label={
                <Typography variant="h6">Bets Yang Sudah Dibuat</Typography>
              }
              labelPlacement="end"
            />
          </Box>
        )}
        <Box p={2}>
          <LoadPremixAutocomplete
            name={searchedPremixDisplay}
            onChange={(premix) => {
              setSearchedPremix(premix?.premixId ?? '')
              setSearchedPremixDisplay(
                premix ? `${premix.premixId} - ${premix.premixName}` : ''
              )
            }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableCell>No. Bets</TableCell>
              <TableCell>Kode Permintaan Ruahan</TableCell>
              <TableCell>Kode + Nama Ruahan</TableCell>
              <TableCell>Quantity Diminta (Batch Size) (kg)</TableCell>
              <TableCell>Qty Dibuat (kg)</TableCell>
              <TableCell>Qty Sisa (kg)</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Tanggal Produksi</TableCell>
              <TableCell>Tanggal Input</TableCell>
              {(isIncompleteOnly || isMixing) && <TableCell>Proses</TableCell>}
              {/* <TableCell>Hapus</TableCell> */}
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12}>
                    <SmoothLoadingIndicator size={120} />
                  </TableCell>
                </TableRow>
              ) : (
                (() => {
                  return batches.map((batch, idx) => (
                    <React.Fragment key={batch.batchNumber || `pending-${batch.productionId}-${idx}`}>
                      <StyledTableRow>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography>{batch.batchNumber || '-'}</Typography>
                            {isSuperuser && !isReadOnly && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEditBatchNumber(batch.batchNumber)
                                }}
                                title="Edit Batch Number"
                                style={{ marginLeft: 4 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography>{batch.productionDisplayId}</Typography>
                          {!isMixing && batch.stockCards && batch.stockCards.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" color="textSecondary">
                                {isMixing
                                  ? 'Kartu Stok Ruahan Masuk: '
                                  : 'Dokumen Terkait: '}
                                {batch.stockCards
                                  .filter(
                                    (sc) =>
                                      !isMixing ||
                                      (sc.type === STOCK_CARD_TYPE_PREMIX &&
                                        sc.inOutDirection === 'IN')
                                  )
                                  .map((sc) => (
                                    <Box
                                      component="span"
                                      key={sc.stockCardId}
                                      style={{
                                        color: sc.isConfirmed
                                          ? 'green'
                                          : 'red',
                                        fontWeight: 'bold',
                                        display: 'inline-block',
                                        marginRight: '8px',
                                      }}
                                    >
                                      {sc.stockCardDisplayId}
                                      {sc.isConfirmed
                                        ? ' (Sudah Konfirmasi)'
                                        : ' (Belum Konfirmasi)'}
                                    </Box>
                                  ))}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {batch.premixId} {batch.premixName ? '-' : ''}{' '}
                            {batch.premixName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDecimalAuto(batch.batchSize ?? 0)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDecimalAuto(batch.producedQuantity ?? 0)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDecimalAuto(batch.currentQuantity ?? 0)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {batch.expiryDate
                              ? new Date(batch.expiryDate ?? '').toDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {batch.productionDate
                              ? new Date(
                                batch.productionDate ?? ''
                              ).toDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {new Date(batch.createdAt).toDateString()}
                          </Typography>
                        </TableCell>
                        {(isIncompleteOnly || isMixing) && (
                          <TableCell>
                            {(isIncompleteOnly || isMixing) && !isBatchCompleted(batch) && (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleSelectBatch(batch)
                                }}
                                disabled={!isProcessBatchEnabled(batch)}
                              >
                                {isProcessBatchEnabled(batch)
                                  ? 'Proses Bets'
                                  : (() => {
                                    const unconfirmedOutCards = getUnconfirmedOutStockCards(batch)

                                    if (unconfirmedOutCards.length > 0) {
                                      const ids = unconfirmedOutCards
                                        .map((sc) => sc.stockCardDisplayId)
                                        .join(', ')
                                      return `MENUNGGU KONFIRMASI KARTU STOK: ${ids}`
                                    }

                                    // If this is a virtual batch (empty placeholder from tblProduction),
                                    // show the "PBOS DIBUAT" copywriting as requested.
                                    if (batch.isVirtual) {
                                      return `MENUNGGU PBOS DIBUAT UNTUK (${batch.productionDisplayId ?? '-'})`
                                    }

                                    // Fallback for real batches without OUT stock cards
                                    return `MENUNGGU KONFIRMASI KARTU STOK KELUAR (${batch.productionDisplayId ?? '-'})`
                                  })()}
                              </Button>
                            )}
                            {isBatchCompleted(batch) &&
                              batch.stockCards?.some(
                                (sc) =>
                                  sc.type === STOCK_CARD_TYPE_PREMIX &&
                                  sc.inOutDirection === 'IN' &&
                                  !sc.isConfirmed
                              ) && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleOpenCancelMixingDialog(batch)
                                  }}
                                  style={{ marginLeft: 8 }}
                                >
                                  Batal
                                </Button>
                              )}
                          </TableCell>
                        )}
                        {/* <TableCell>
                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      onClick={() => {
                        setIsConfirmDeleteOpen(true)
                        setBatchData(batch)
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </TableCell> */}
                      </StyledTableRow>
                    </React.Fragment>
                  ))
              })())}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          onPageChange={(_, newPage) => setCurrentPage(newPage + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setCurrentPage(1)
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Baris per halaman:"
        />
      </Paper>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isBatchSelected}
        onClose={handleDialogClose}
      >
        <Box p={4}>
          <Box mb={2}>
            <Typography variant="h5">
              No. Bets: {batchData.batchNumber}
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant="h5">
              Qty Diminta ({premixDepartment?.toLowerCase() === 'ot' ? 'kapsul' : 'kg'}):{' '}
              {batchData.batchSize}
            </Typography>
          </Box>
          <Box>
            <TextField
              label={`Quantity Dibuat (${premixDepartment?.toLowerCase() === 'ot' ? 'kapsul' : 'kg'
                })`}
              variant="outlined"
              type="number"
              onChange={(e) =>
                handleBatchFieldChange(e.target.value, 'producedQuantity')
              }
              value={batchData.producedQuantity || ''}
            />
          </Box>
          <Box mt={2}>
            <DatePicker
              label="Tanggal Produksi"
              variant="inline"
              inputVariant="outlined"
              format="dd/MM/yyyy"
              value={batchData.productionDate}
              onChange={(date) =>
                handleBatchFieldChange(date, 'productionDate')
              }
              fullWidth
            />
          </Box>
          <Box mt={2}>
            <DatePicker
              label="Tanggal Expiry"
              variant="inline"
              inputVariant="outlined"
              format="dd/MM/yyyy"
              value={batchData.expiryDate}
              onChange={(date) => handleBatchFieldChange(date, 'expiryDate')}
              fullWidth
            />
          </Box>
          <Box mt={2}>
            <Button variant="contained" color="primary" onClick={updateBatch} disabled={isLoading}>
              Tandai Selesai Mixing
            </Button>
          </Box>
        </Box>
        {/* <BatchHistory batchNumber={batchData.batchNumber} /> */}
        {/* <BatchForm
          batchData={batchData}
          updateBatch={updateBatch}
          isLoading={isLoading}
          handleBatchFieldChange={handleBatchFieldChange}
          isEditing
          isQuantityInitiallyNull={isQuantityInitiallyNull}
        /> */}
      </Dialog>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog
        open={isEditBatchDialogOpen}
        onClose={() => setIsEditBatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Batch Number</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              fullWidth
              label="Current Batch Number"
              value={editingBatchNumber}
              disabled
              margin="normal"
            />
            <TextField
              fullWidth
              label="New Batch Number"
              value={newBatchNumber}
              onChange={(e) => setNewBatchNumber(e.target.value)}
              margin="normal"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditBatchDialogOpen(false)}>
            Batal
          </Button>
          <Button
            onClick={handleUpdateBatchNumber}
            color="primary"
            variant="contained"
            disabled={isLoading || !newBatchNumber || newBatchNumber === editingBatchNumber}
          >
            Simpan
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        title=""
        open={isConfirmDeleteOpen}
        setOpen={setIsConfirmDeleteOpen}
        onConfirm={() => handleDeleteBatch()}
      >
        <Typography variant="h6">
          {`Yakin ingin menghapus ${batchData.batchNumber} dari system?`}
        </Typography>
      </ConfirmDialog>
      <ConfirmDialog
        title="Batal Tandai Selesai Mixing?"
        open={isConfirmCancelMixingOpen}
        setOpen={setIsConfirmCancelMixingOpen}
        onConfirm={() => handleCancelMixingComplete()}
      >
        <Box>
          <Typography variant="body1">
            No. Bets: {batchToCancel?.batchNumber}
          </Typography>
          <Typography variant="body1">
            Qty Diminta: {batchToCancel?.batchSize}
          </Typography>
          <Typography variant="body1">
            Qty Dibuat: {batchToCancel?.producedQuantity}
          </Typography>
          <Typography variant="body1">
            Tanggal Produksi:{' '}
            {batchToCancel?.productionDate
              ? new Date(batchToCancel.productionDate).toLocaleDateString()
              : '-'}
          </Typography>
          <Typography variant="body1">
            Tanggal Expiry:{' '}
            {batchToCancel?.expiryDate
              ? new Date(batchToCancel.expiryDate).toLocaleDateString()
              : '-'}
          </Typography>
          <Box mt={2}>
            <Typography variant="h6">
              Apakah anda yakin ingin membatalkan tandai selesai mixing? Kartu
              Stok Ruahan Masuk:{' '}
              {batchToCancel?.stockCards?.find(
                (sc) =>
                  sc.type === STOCK_CARD_TYPE_PREMIX &&
                  sc.inOutDirection === 'IN' &&
                  !sc.isConfirmed
              )?.stockCardDisplayId || ''}{' '}
              (Belum Konfirmasi) akan dihapus.
            </Typography>
          </Box>
        </Box>
      </ConfirmDialog>
    </Box>
  )
}
